/** Acota un segmento de 2 dígitos al rango `[min, max]`, preservando el cero a
 * la izquierda (p.ej. `clampSegment('00', 1, 31)` → `'01'`). */
function clampSegment(twoDigits: string, min: number, max: number): string {
  const n = Number(twoDigits);
  const clamped = Math.min(Math.max(n, min), max);
  return String(clamped).padStart(2, '0');
}

/** ¿`year` es bisiesto? Regla gregoriana completa (divisible entre 4, salvo
 * los múltiplos de 100 que no lo son de 400: 2000 sí, 1900 no). */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Días máximos del mes `mm` (1-12). Cuando el año todavía no está completo se
 * asume el caso más permisivo (febrero → 29) para no pelear con el tecleo: en
 * cuanto el año llega, el clamp se re-aplica con el valor real.
 */
function daysInMonth(mm: number, yyyy: number | null): number {
  if (mm === 2) return yyyy !== null && !isLeapYear(yyyy) ? 28 : 29;
  return [4, 6, 9, 11].includes(mm) ? 30 : 31;
}

/**
 * Enmascara la entrada a `DD/MM/AAAA` insertando `/` y, además, **acota en vivo**
 * cada segmento a valores posibles para que no se puedan teclear fechas
 * imposibles (día > 31, mes > 12, 30 de febrero):
 *
 * - **Día:** un primer dígito 4-9 se auto-completa a `0X` (p.ej. `7` → `07`) y
 *   avanza al mes; con dos dígitos se acota al rango 01-31.
 * - **Mes:** un primer dígito 2-9 → `0X`; con dos dígitos se acota a 01-12.
 * - **Año:** hasta 4 dígitos, sin acotar (el rango de años lo valida el
 *   consumidor, p.ej. `birthDateField`).
 * - **Día vs. mes:** en cuanto el mes está completo, el día se re-acota a los
 *   días reales de ese mes (`31/04` → `30/04`, `30/02` → `29/02`); al completar
 *   el año se re-aplica con la regla de bisiesto (`29/02/2023` → `28/02/2023`).
 *
 * El rango de fechas (futura, edad mínima) sigue siendo responsabilidad del
 * consumidor; esto evita que se pueda *teclear* una fecha que no existe.
 *
 * @example maskDate('12042000') => '12/04/2000'
 * @example maskDate('7')        => '07'          // día 4-9 se auto-completa
 * @example maskDate('3939')     => '30/09'       // día 39→31→30 (septiembre)
 * @example maskDate('30022024') => '29/02/2024'  // bisiesto
 * @example maskDate('30022023') => '28/02/2023'  // no bisiesto
 */
export function maskDate(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  let i = 0;
  const parts: string[] = [];

  // DÍA
  if (i < digits.length) {
    const d1 = digits[i] as string;
    if (d1 >= '4') {
      parts.push(`0${d1}`); // 4-9 → 04-09, auto-avanza
      i += 1;
    } else if (i + 1 < digits.length) {
      parts.push(clampSegment(digits.slice(i, i + 2), 1, 31));
      i += 2;
    } else {
      parts.push(d1); // 0-3, aún escribiendo el día
      i += 1;
    }
  }

  // MES (solo cuando el día ya está completo con 2 dígitos)
  if (i < digits.length && parts[0]?.length === 2) {
    const m1 = digits[i] as string;
    if (m1 >= '2') {
      parts.push(`0${m1}`); // 2-9 → 02-09, auto-avanza
      i += 1;
    } else if (i + 1 < digits.length) {
      parts.push(clampSegment(digits.slice(i, i + 2), 1, 12));
      i += 2;
    } else {
      parts.push(m1); // 0-1, aún escribiendo el mes
      i += 1;
    }
  }

  // AÑO (hasta 4 dígitos, sin acotar)
  if (i < digits.length && parts.length === 2 && parts[1]?.length === 2) {
    parts.push(digits.slice(i, i + 4));
  }

  // DÍA vs. MES: con el mes ya completo, el día no puede exceder los días
  // reales de ese mes. Se re-acota aquí (y no dentro del bloque DÍA) porque el
  // mes/año se teclean *después* del día — al llegar el año, un 29/02 escrito
  // sobre un año no bisiesto tiene que corregirse a 28.
  const [dayPart, monthPart, yearPart] = parts;
  if (dayPart?.length === 2 && monthPart?.length === 2) {
    const yyyy = yearPart?.length === 4 ? Number(yearPart) : null;
    const max = daysInMonth(Number(monthPart), yyyy);
    parts[0] = clampSegment(dayPart, 1, max);
  }

  return parts.join('/');
}
